import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { gmailApi, propertiesApi, formatCurrency, teamApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Mail, Link2, Unlink, RefreshCw, Loader2, AlertCircle, CheckCircle2, Inbox, FileText, Building2, Users, UserPlus, Trash2, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import AppLayout from '@/components/AppLayout';
import ReviewQueue from '@/components/ReviewQueue';

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [gmailStatus, setGmailStatus] = useState({ configured: false, connected: false, email: null, requires_reauth: false, last_email: null });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [candidates, setCandidates] = useState(null);
  const [syncMessage, setSyncMessage] = useState('');
  const [properties, setProperties] = useState([]);
  const [importedReports, setImportedReports] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [teamRole, setTeamRole] = useState({ role: 'owner' }); // role: 'owner' | 'member'
  const [leavingTeam, setLeavingTeam] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const [statusRes, propsRes] = await Promise.all([
        gmailApi.status(),
        propertiesApi.getAll('active'),
      ]);
      setGmailStatus(statusRes.data);
      setProperties(propsRes.data);
    } catch (error) {
      console.error('Failed to fetch Gmail status:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchImports = useCallback(async () => {
    try {
      const res = await gmailApi.imports();
      setImportedReports(res.data.imports || []);
    } catch (error) {
      // Silently fail — imports section is informational
    }
  }, []);

  const fetchTeam = useCallback(async () => {
    try {
      const [statusRes, membersRes, invitesRes] = await Promise.all([
        teamApi.status(),
        teamApi.members(),
        teamApi.pendingInvites(),
      ]);
      setTeamRole(statusRes.data || { role: 'owner' });
      setTeamMembers(membersRes.data || []);
      setPendingInvites(invitesRes.data || []);
    } catch (error) {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchImports();
    fetchTeam();
  }, [fetchStatus, fetchImports, fetchTeam]);

  // Handle OAuth redirect result
  useEffect(() => {
    const gmailParam = searchParams.get('gmail');
    if (gmailParam === 'connected') {
      toast.success('Gmail connected successfully!');
      fetchStatus();
      searchParams.delete('gmail');
      setSearchParams(searchParams, { replace: true });
    } else if (gmailParam === 'error') {
      const reason = searchParams.get('reason') || 'Unknown error';
      toast.error(`Gmail connection failed: ${reason.replace(/_/g, ' ')}`);
      searchParams.delete('gmail');
      searchParams.delete('reason');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, fetchStatus]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const response = await gmailApi.connect();
      window.location.href = response.data.auth_url;
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to start Gmail connection';
      toast.error(detail);
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await gmailApi.disconnect();
      setGmailStatus({ ...gmailStatus, connected: false, email: null });
      setCandidates(null);
      setSyncMessage('');
      toast.success('Gmail disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Gmail');
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setCandidates(null);
    setSyncMessage('');
    try {
      const response = await gmailApi.sync();
      setCandidates(response.data.candidates);
      setSyncMessage(response.data.message);
      if (response.data.candidates.length > 0) {
        toast.success(`Found ${response.data.candidates.length} PDF(s) to review`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to sync Gmail';
      toast.error(detail);
      setSyncMessage('');
      if (error.response?.status === 401) {
        // Token died — refresh status to show reauth banner
        fetchStatus();
      }
    } finally {
      setSyncing(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const response = await teamApi.invite(inviteEmail.trim());
      // Show appropriate message based on email delivery status
      if (response.data.email_sent) {
        toast.success(`Invitation email sent to ${inviteEmail}`);
      } else {
        toast.success(`Invitation created for ${inviteEmail}. They can sign up and accept in Settings.`);
      }
      setInviteEmail('');
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveMember = async (id) => {
    try {
      await teamApi.removeMember(id);
      toast.success('Team member removed');
      fetchTeam();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handleAcceptInvite = async (id) => {
    try {
      await teamApi.acceptInvite(id);
      toast.success('Invite accepted! You now have access to their portfolio.');
      fetchTeam();
      // Reload the page to reflect new data access
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept invite');
    }
  };

  const handleLeaveTeam = async () => {
    setLeavingTeam(true);
    try {
      await teamApi.leave();
      toast.success('You have left the team');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to leave team');
      setLeavingTeam(false);
    }
  };

  const statusBadge = (status) => {
    if (status === 'parsed') return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Parsed</Badge>;
    if (status === 'failed') return <Badge className="bg-red-100 text-red-700 border-red-200">Failed</Badge>;
    return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Pending</Badge>;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="spinner w-8 h-8"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-8 max-w-4xl" data-testid="settings-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-ink font-display tracking-tight">Settings</h1>
          <p className="mt-1 text-muted-brand">Manage integrations and preferences</p>
        </div>

        {/* Gmail Integration Section */}
        <Card className="border-line" data-testid="gmail-integration-card">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-error/10 rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5 text-error" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold font-display">Gmail integration</CardTitle>
                <p className="text-sm text-muted-brand mt-0.5">
                  Automatically detect letting agent PDF reports from your inbox
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {!gmailStatus.configured ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="gmail-not-configured">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Gmail integration not configured</p>
                    <p className="text-sm text-amber-700 mt-1">
                      To enable Gmail integration, add your Google OAuth credentials
                      (GOOGLE_CLIENT_ID, GOOGLE_CLIENT_SECRET, GOOGLE_REDIRECT_URI)
                      to the backend environment.
                    </p>
                  </div>
                </div>
              </div>
            ) : !gmailStatus.connected ? (
              <div data-testid="gmail-disconnected">
                {/* Re-auth required banner */}
                {gmailStatus.requires_reauth && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4" data-testid="gmail-reauth-required">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-red-800">Re-authentication required</p>
                        <p className="text-sm text-red-700 mt-1">
                          Your Gmail connection for <strong>{gmailStatus.last_email}</strong> has expired or been revoked.
                          Please reconnect to resume syncing letting agent reports.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="bg-slate-50 rounded-lg p-6 text-center">
                  <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h3 className="text-sm font-semibold text-ink font-display mb-1">Connect your Gmail</h3>
                  <p className="text-sm text-slate-500 mb-4 max-w-md mx-auto">
                    Connect your Gmail account to automatically detect monthly letting agent
                    PDF reports. We only request read-only access to your inbox.
                  </p>
                  <Button
                    onClick={handleConnect}
                    disabled={connecting}
                    className="bg-slate-900 hover:bg-slate-800"
                    data-testid="gmail-connect-btn"
                  >
                    {connecting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Link2 className="w-4 h-4 mr-2" />
                    )}
                    {connecting ? 'Connecting...' : 'Connect Gmail'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-6" data-testid="gmail-connected">
                <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-sm font-medium text-emerald-800">Connected</p>
                      <p className="text-sm text-emerald-600" data-testid="gmail-connected-email">
                        {gmailStatus.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={handleSync}
                      disabled={syncing}
                      className="bg-slate-900 hover:bg-slate-800"
                      data-testid="gmail-sync-btn"
                    >
                      {syncing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      {syncing ? 'Syncing...' : 'Sync Emails'}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={handleDisconnect}
                      disabled={disconnecting}
                      className="text-slate-500 hover:text-red-600"
                      data-testid="gmail-disconnect-btn"
                    >
                      {disconnecting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Unlink className="w-4 h-4 mr-1" />
                      )}
                      Disconnect
                    </Button>
                  </div>
                </div>

                {/* Sync loading */}
                {syncing && (
                  <div className="flex items-center justify-center py-8">
                    <div className="flex items-center gap-3 text-slate-500">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-sm">Searching your inbox for letting agent reports...</span>
                    </div>
                  </div>
                )}

                {/* No results */}
                {!syncing && syncMessage && candidates && candidates.length === 0 && (
                  <div className="bg-slate-50 rounded-lg p-6 text-center" data-testid="gmail-no-results">
                    <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">{syncMessage}</p>
                  </div>
                )}

                {/* Review queue */}
                {!syncing && candidates && candidates.length > 0 && (
                  <ReviewQueue
                    candidates={candidates}
                    properties={properties}
                    onImported={() => fetchImports()}
                    onDismissed={() => {}}
                  />
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Imported Reports Section */}
        <Card className="border-slate-200 mt-6" data-testid="gmail-imported-reports-card">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold font-display">Imported Reports</CardTitle>
                <p className="text-sm text-slate-500 mt-0.5">
                  Reports imported from Gmail into your properties
                </p>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {importedReports.length === 0 ? (
              <div className="bg-slate-50 rounded-lg p-6 text-center">
                <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No reports imported from Gmail yet</p>
                <p className="text-xs text-slate-400 mt-1">
                  Use "Sync Emails" above to detect and import letting agent PDFs
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto" data-testid="imported-reports-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">File</th>
                      <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Property</th>
                      <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Period</th>
                      <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Income</th>
                      <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Agent Fee</th>
                      <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Repairs</th>
                      <th className="text-left py-2.5 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {importedReports.map((report) => (
                      <tr
                        key={report.id}
                        className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors"
                        data-testid={`imported-report-${report.id}`}
                      >
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-red-400 flex-shrink-0" />
                            <span className="text-slate-900 font-medium truncate max-w-[180px]" title={report.filename}>
                              {report.filename}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-1.5">
                            <Building2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                            <span className="text-slate-700 truncate max-w-[200px]" title={report.property_address}>
                              {report.property_address.split(',')[0].trim()}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-3">
                          <span className="text-slate-700">{report.month} {report.year}</span>
                        </td>
                        <td className="py-3 px-3">
                          <span className="text-emerald-600 font-medium tabular-nums">
                            {report.rental_income != null ? formatCurrency(report.rental_income) : '-'}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <span className="text-slate-600 tabular-nums">
                            {report.letting_agent_fee != null ? formatCurrency(report.letting_agent_fee) : '-'}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <span className="text-slate-600 tabular-nums">
                            {report.repairs_maintenance != null ? formatCurrency(report.repairs_maintenance) : '-'}
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          {statusBadge(report.status)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
        {/* Team Access Section */}
        <Card className="border-slate-200 mt-6" data-testid="team-access-card">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-50 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-violet-500" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold font-display">Team Access</CardTitle>
                <p className="text-sm text-slate-500 mt-0.5">
                  {teamRole.role === 'member' 
                    ? `You're a member of ${teamRole.owner_name || teamRole.owner_email}'s portfolio`
                    : 'Invite others to view and manage your portfolio'
                  }
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Pending invites for this user (as invitee) - show for any role */}
            {pendingInvites.length > 0 && (
              <div className="space-y-2 mb-4">
                <h4 className="text-sm font-medium text-muted-brand font-display">Pending Invitations</h4>
                {pendingInvites.map(inv => (
                  <div key={inv.id} className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-amber-900">
                        {inv.owner_name || inv.owner_email} has invited you to their portfolio
                      </p>
                      <p className="text-xs text-amber-700">{inv.owner_email}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleAcceptInvite(inv.id)}
                      className="bg-slate-900 hover:bg-slate-800"
                      data-testid={`accept-invite-${inv.id}`}
                    >
                      Accept
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Member view: show current team membership with Leave button */}
            {teamRole.role === 'member' && (
              <div className="bg-violet-50 border border-violet-200 rounded-lg px-4 py-4" data-testid="team-member-status">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-semibold text-violet-600">
                        {(teamRole.owner_name || teamRole.owner_email || 'O')[0].toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-violet-900">
                        {teamRole.owner_name || 'Account Owner'}
                      </p>
                      <p className="text-xs text-violet-600">{teamRole.owner_email}</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLeaveTeam}
                    disabled={leavingTeam}
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                    data-testid="leave-team-btn"
                  >
                    {leavingTeam ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    ) : (
                      <LogOut className="w-4 h-4 mr-1" />
                    )}
                    Leave Team
                  </Button>
                </div>
                <p className="text-xs text-violet-700 mt-3">
                  You have full access to view and edit this portfolio's properties, reports, and expenses.
                </p>
              </div>
            )}

            {/* Owner view: invite form */}
            {teamRole.role === 'owner' && (
              <form onSubmit={handleInvite} className="flex gap-2" data-testid="team-invite-form">
                <Input
                  type="email"
                  placeholder="Enter email address to invite..."
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="flex-1"
                  data-testid="team-invite-email"
                />
                <Button
                  type="submit"
                  disabled={inviting || !inviteEmail.trim()}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid="team-invite-btn"
                >
                  {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4 mr-1" />}
                  Invite
                </Button>
              </form>
            )}

            {/* Owner view: team members list */}
            {teamRole.role === 'owner' && (
              <>
                {teamMembers.length > 0 ? (
                  <div className="space-y-2" data-testid="team-members-list">
                    {teamMembers.map(member => (
                      <div key={member.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center">
                            <span className="text-xs font-semibold text-violet-600">
                              {(member.member_name || member.member_email)[0].toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">
                              {member.member_name || member.member_email}
                            </p>
                            <p className="text-xs text-slate-500">{member.member_email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge
                            variant="outline"
                            className={member.status === 'accepted'
                              ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                              : 'bg-amber-100 text-amber-700 border-amber-200'
                            }
                          >
                            {member.status === 'accepted' ? 'Active' : 'Pending'}
                          </Badge>
                          <button
                            onClick={() => handleRemoveMember(member.id)}
                            className="p-1.5 rounded hover:bg-red-100 transition-colors"
                            data-testid={`remove-member-${member.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-2">
                    No team members yet. Invite someone to share access to your properties and reports.
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
